import { toInputDate } from "./date";
import type { FollowUp, RealEstateCase } from "./types";

export type OwnerSaleSequenceStep = {
  step: number;
  offsetDays: number;
  title: string;
  shortTitle: string;
  purpose: string;
  goal: string;
  recommendedNote: string;
  softGoal: string;
  principle: string;
};

export const OWNER_SALE_SEQUENCE_STEPS: OwnerSaleSequenceStep[] = [
  {
    step: 1,
    offsetDays: 3,
    title: "1. follow-up - majiteľ predáva sám",
    shortTitle: "1. follow-up - predáva sám",
    purpose: "Zistiť prvú reakciu trhu",
    goal: "Zistiť prvú reakciu trhu.",
    recommendedNote:
      "Dobrý deň, ozývam sa len krátko, ako sme sa dohodli. Chcem sa opýtať, či už prišli prvé telefonáty alebo záujemcovia a aké boli reakcie na cenu.",
    softGoal: "Získať prvé signály z trhu bez predaja služby.",
    principle: "Nič nepredávať, netlačiť na spoluprácu, iba zbierať informácie a počúvať."
  },
  {
    step: 2,
    offsetDays: 7,
    title: "2. follow-up - majiteľ predáva sám",
    shortTitle: "2. follow-up - predáva sám",
    purpose: "Vyhodnotiť kvalitu záujmu",
    goal: "Vyhodnotiť kvalitu záujmu.",
    recommendedNote:
      "Po týždni sa už väčšinou ukáže, či trh cenu a prezentáciu prijal. Mali ste už reálne obhliadky alebo skôr len telefonáty a otázky?",
    softGoal: "Ukázať odborný pohľad cez otázky a výsledky.",
    principle: "Nepoučovať, nehovoriť, že cena je zlá, pýtať sa na kvalitu reakcií."
  },
  {
    step: 3,
    offsetDays: 14,
    title: "3. follow-up - majiteľ predáva sám",
    shortTitle: "3. follow-up - predáva sám",
    purpose: "Otvoriť tému stratégie",
    goal: "Otvoriť tému stratégie.",
    recommendedNote:
      "Po dvoch týždňoch už máme zvyčajne dosť údajov na prvé vyhodnotenie. Ak sa nehnuteľnosť pozerá, ale ľudia nechodia na obhliadky, býva problém najčastejšie v cene, prezentácii alebo zacielení. Ako to zatiaľ vyzerá u vás?",
    softGoal: "Ponúknuť hodnotu a pomenovať realitu bez útoku.",
    principle: "Stále netlačiť na zmluvu, viesť rozhovor cez dáta a pozorovanie trhu."
  },
  {
    step: 4,
    offsetDays: 21,
    title: "4. follow-up - majiteľ predáva sám",
    shortTitle: "4. follow-up - predáva sám",
    purpose: "Navrhnúť konkrétnu pomoc alebo stretnutie",
    goal: "Navrhnúť konkrétnu pomoc alebo stretnutie.",
    recommendedNote:
      "Po troch týždňoch už väčšinou vidíme, či súčasný spôsob predaja funguje. Ak chcete, môžeme si na 20 minút prejsť reakcie trhu, cenu a možnosti, čo by sa dalo zlepšiť. Bez záväzku.",
    softGoal: "Ponúknuť krátke stretnutie a konkrétnu pomoc bez záväzku.",
    principle: "Konkrétne CTA bez nátlaku a bez kritizovania doterajšieho predaja."
  }
];

export const getOwnerSaleSequenceStep = (step: number | undefined) =>
  OWNER_SALE_SEQUENCE_STEPS.find((sequenceStep) => sequenceStep.step === step);

export const getNextOwnerSaleSequenceStep = (step: number | undefined) =>
  getOwnerSaleSequenceStep((step ?? 0) + 1);

export const isOwnerSaleSequenceActive = (caseItem: RealEstateCase) =>
  caseItem.ownerSaleSequence?.status === "Aktívna";

export const isOwnerSaleSequenceEnded = (caseItem: RealEstateCase) =>
  caseItem.ownerSaleSequence?.status === "Ukončená";

export const getCurrentOwnerSaleSequenceStep = (caseItem: RealEstateCase) => {
  if (!caseItem.ownerSaleSequence) {
    return undefined;
  }

  const storedStep = Math.max(1, Math.min(4, caseItem.ownerSaleSequence.currentStep || 1));
  return getOwnerSaleSequenceStep(storedStep);
};

export const addDaysToInputDate = (days: number, baseDate = new Date()) => {
  const plannedDate = new Date(baseDate);
  plannedDate.setDate(plannedDate.getDate() + days);
  return toInputDate(plannedDate);
};

export const createOwnerSaleFollowUpDraft = (
  step: OwnerSaleSequenceStep,
  preferredTime: string,
  baseDate = new Date()
): FollowUp => ({
  date: addDaysToInputDate(step.offsetDays, baseDate),
  time: preferredTime,
  purpose: step.title
});

export const ownerSaleProgressLabel = (caseItem: RealEstateCase) => {
  const completed = caseItem.ownerSaleSequence?.completedContacts ?? 0;
  return `${completed}/4 dokončené`;
};
